import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, Router } from '@angular/router';
import { AuthRepository, UserRole } from '../auth.repository';

/**
 * Guard that restricts access to supervisor routes.
 * Only allows: SuperUser, SysAdmin, CourseAdmin, and Supervisor roles.
 * Explicitly blocks: Student, Advisor, Translator, and all other roles.
 * 
 * Updated to support RBAC Permission System while maintaining backward compatibility.
 */
@Injectable({
  providedIn: 'root'
})
export class SupervisorRoleGuard implements CanActivate, CanActivateChild {
  
  constructor(
    private authRepository: AuthRepository,
    private router: Router
  ) {}

  canActivate(): boolean {
    return this.hasSupervisorAccess();
  }

  canActivateChild(): boolean {
    return this.hasSupervisorAccess();
  }

  private hasSupervisorAccess(): boolean {
    const user = this.authRepository.getUser();
    
    // If no user, redirect to sign-in
    if (!user) {
      this.router.navigate(['/sign-in']);
      return false;
    }

    // NEW: Check is_admin flag first (RBAC superuser bypass)
    if (user.is_admin) {
      return true;
    }

    // NEW: Check permissions if available (RBAC system)
    // Supervisor access requires mod_students permission
    if (user.permissions && user.permissions.length > 0) {
      const supervisorPermissions = ['mod_students', 'do_approve_step', 'do_reject_step'];
      const hasSupervisorPermission = user.permissions.some(p => supervisorPermissions.includes(p));
      if (hasSupervisorPermission) {
        return true;
      }
    }

    // FALLBACK: Legacy role check for backward compatibility
    // Only these roles are allowed to access supervisor routes
    const allowedRoles = [
      UserRole.SuperUser,      // 1
      UserRole.SysAdmin,       // 2
      UserRole.CourseAdmin,    // 3
      UserRole.Supervisor,     // 11
    ];

    // Explicitly check if user role is in allowed list
    const hasAccess = allowedRoles.includes(user.role);
    
    // Explicitly block Student role (5) and any other role not in allowed list
    if (!hasAccess) {
      // Log attempt for security monitoring
      console.warn(`Access denied: User role ${user.role} (${this.getRoleName(user.role)}) attempted to access supervisor route`);
      
      // Redirect to home if user doesn't have required role
      this.router.navigate(['/home']);
      return false;
    }

    return true;
  }

  /**
   * Helper method to get role name for logging
   */
  private getRoleName(role: number): string {
    const roleNames: { [key: number]: string } = {
      1: 'SuperUser',
      2: 'SysAdmin',
      3: 'CourseAdmin',
      5: 'Student',
      6: 'Advisor',
      7: 'Translator',
      11: 'Supervisor',
    };
    return roleNames[role] || `Unknown(${role})`;
  }
}


